import { ArrowDownUp, Database, Radio } from 'lucide-react';
import type { PortfolioHolding, PortfolioOverview } from '../App';

type HoldingsTableProps = {
  overview: PortfolioOverview;
  fetchedAt: string;
};

export function HoldingsTable({ overview, fetchedAt }: HoldingsTableProps) {
  return (
    <section className="dashboard-panel holdings-panel" aria-labelledby="holdings-heading">
      <div className="panel-heading">
        <div>
          <h2 id="holdings-heading">Holdings</h2>
          <p>Live values use latest quotes; rows without quotes fall back to broker market value.</p>
        </div>
        <ArrowDownUp size={18} aria-hidden="true" />
      </div>

      <div className="table-wrap">
        <table className="holdings-table">
          <thead>
            <tr>
              <th scope="col">Symbol</th>
              <th scope="col">Role</th>
              <th scope="col" className="numeric">
                Quantity
              </th>
              <th scope="col" className="numeric">
                Latest price
              </th>
              <th scope="col" className="numeric">
                Live value
              </th>
              <th scope="col">Quote source</th>
            </tr>
          </thead>
          <tbody>
            {overview.holdings.length === 0 ? (
              <tr className="empty-row">
                <td colSpan={6}>No holdings in the current overview.</td>
              </tr>
            ) : (
              overview.holdings.map((holding) => <HoldingRow key={holding.symbol} holding={holding} fetchedAt={fetchedAt} />)
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function HoldingRow({ holding, fetchedAt }: { holding: PortfolioHolding; fetchedAt: string }) {
  const quotePrice = typeof holding.latestQuotePrice === 'number' ? holding.latestQuotePrice : null;
  const hasQuote = quotePrice != null;
  const liveValue = quotePrice != null
    ? quotePrice * holding.quantity
    : typeof holding.brokerMarketValue === 'number'
      ? holding.brokerMarketValue
      : null;
  const impliedBrokerPrice =
    !hasQuote && typeof holding.brokerMarketValue === 'number' && holding.quantity !== 0
      ? holding.brokerMarketValue / holding.quantity
      : null;
  const quoteAgeSeconds = holding.quoteTime ? secondsBetween(holding.quoteTime, fetchedAt) : null;
  const quoteAge = quoteAgeSeconds == null ? null : formatAge(quoteAgeSeconds);
  const stale = quoteAgeSeconds != null && holding.staleAfterSeconds != null && quoteAgeSeconds > holding.staleAfterSeconds;

  return (
    <tr>
      <td>
        <div className="symbol-cell">
          <strong>{holding.symbol}</strong>
          <span>{holding.bucket}</span>
        </div>
      </td>
      <td>
        <div className="role-cell">
          <strong>{holding.role}</strong>
          <span>{holding.name}</span>
        </div>
      </td>
      <td className="numeric">{formatQuantity(holding.quantity)}</td>
      <td className="numeric">
        {quotePrice != null ? formatMoney(quotePrice) : impliedBrokerPrice == null ? 'Missing' : formatMoney(impliedBrokerPrice)}
        {!hasQuote && impliedBrokerPrice != null ? <span className="cell-note">broker implied</span> : null}
      </td>
      <td className="numeric">
        {liveValue == null ? 'Missing' : formatMoney(liveValue)}
        {!hasQuote && liveValue != null ? <span className="cell-note">broker fallback</span> : null}
      </td>
      <td>
        <div className={`source-chip ${hasQuote ? (stale ? 'stale' : 'live') : liveValue == null ? 'missing' : 'fallback'}`}>
          {hasQuote ? <Radio size={14} aria-hidden="true" /> : <Database size={14} aria-hidden="true" />}
          <span>
            {hasQuote
              ? `${holding.quoteProvider ?? 'unknown'} · ${quoteAge ?? 'unknown age'} old${stale ? ' · stale' : ''}`
              : liveValue == null
                ? 'Quote source missing'
                : 'Broker market value'}
          </span>
        </div>
      </td>
    </tr>
  );
}

function secondsBetween(startIso: string, endIso: string): number {
  const start = Date.parse(startIso);
  const end = Date.parse(endIso);
  if (Number.isNaN(start) || Number.isNaN(end)) return Number.POSITIVE_INFINITY;
  return Math.max(0, Math.floor((end - start) / 1000));
}

function formatMoney(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

function formatQuantity(value: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 4 }).format(value);
}

function formatAge(seconds: number): string {
  if (!Number.isFinite(seconds)) return 'unknown';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86_400)}d`;
}
